import { useMemo, useRef } from "react";
import { asNumber, formatValue } from "../../lib/kernelFormat.js";
import { boundedLimit } from "../../lib/decisionScales.js";
import { KernelEmpty, useElementSize } from "./shared.jsx";

export const kind = "slopegraph";

function tone(delta, favorable) {
    if (!["up", "down"].includes(favorable) || delta === 0) return "var(--muted)";
    const good = favorable === "down" ? delta < 0 : delta > 0;
    return good ? "var(--green)" : "var(--red, #c0392b)";
}

function spreadLabels(positions, gap, min, max) {
    if (positions.length < 2) return positions;
    const order = positions.map((value, index) => ({ value, index })).sort((a, b) => a.value - b.value);
    const output = positions.slice();
    output[order[0].index] = Math.max(min, output[order[0].index]);
    for (let i = 1; i < order.length; i += 1) {
        const previous = order[i - 1].index;
        const current = order[i].index;
        output[current] = Math.max(output[current], output[previous] + gap);
    }
    const overflow = output[order.at(-1).index] - max;
    if (overflow > 0) {
        output[order.at(-1).index] = max;
        for (let i = order.length - 2; i >= 0; i -= 1) {
            const current = order[i].index;
            const next = order[i + 1].index;
            output[current] = Math.min(output[current], output[next] - gap);
        }
    }
    return output;
}

export function Component({ spec, data, fillHeight = false, onFocus }) {
    const wrapperRef = useRef(null);
    const { width } = useElementSize(wrapperRef);
    const labelField = spec.labelField || "label";
    const leftField = spec.leftField || "previous";
    const rightField = spec.rightField || "current";
    const maxItems = boundedLimit(spec.maxItems, 12, 20, 2);
    const rows = useMemo(() => (Array.isArray(data) ? data : [])
        .map((row) => ({
            label: String(row?.[labelField] ?? "Unlabelled"),
            left: asNumber(row?.[leftField]),
            right: asNumber(row?.[rightField]),
        }))
        .filter((row) => row.left != null && row.right != null)
        .sort((a, b) => Math.max(Math.abs(b.left), Math.abs(b.right)) - Math.max(Math.abs(a.left), Math.abs(a.right)))
        .slice(0, maxItems), [data, labelField, leftField, rightField, maxItems]);
    if (!rows.length) return <KernelEmpty message={spec.empty || "Nothing to compare"} />;

    const minimumHeight = Math.max(120, 30 + (rows.length - 1) * 12);
    const height = boundedLimit(spec.height, 24 + rows.length * 18, 500, minimumHeight);
    const format = spec.format || "number";
    const padTop = 22;
    const padBottom = 8;
    const plotHeight = height - padTop - padBottom;
    const values = rows.flatMap((row) => [row.left, row.right]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const y = (value) => padTop + ((max - value) / span) * plotHeight;
    const leftX = Math.max(30, Math.min(72, width * 0.22));
    const rightX = Math.min(width - 4, Math.max(leftX + 24, width - Math.max(60, Math.min(210, width * 0.42))));
    const leftLabels = spreadLabels(rows.map((row) => y(row.left)), 12, padTop, height - padBottom);
    const rightLabels = spreadLabels(rows.map((row) => y(row.right)), 12, padTop, height - padBottom);
    const focused = spec.focusValue == null ? null : String(spec.focusValue);
    const interactive = Boolean(onFocus);
    const favorable = ["up", "down", "neutral"].includes(spec.favorable) ? spec.favorable : "neutral";

    return (
        <div className={fillHeight ? "flex h-full min-h-0 flex-col" : ""}>
            {spec.title ? <div className="mb-[4px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">{spec.title}</div> : null}
            <div ref={wrapperRef} className="w-full" style={{ height: `${height}px` }}>
                {width > 0 ? (
                    <svg width="100%" height={height} role={interactive ? "group" : "img"} aria-label={spec.title || "Two-period slopegraph"}>
                        <text x={leftX} y="10" textAnchor="end" className="fill-[color:var(--muted-soft)]" style={{ fontSize: 9 }}>{spec.leftLabel || "Previous"}</text>
                        <text x={rightX} y="10" textAnchor="start" className="fill-[color:var(--muted-soft)]" style={{ fontSize: 9 }}>{spec.rightLabel || "Current"}</text>
                        <line x1={leftX} y1={padTop} x2={leftX} y2={height - padBottom} stroke="var(--line)" />
                        <line x1={rightX} y1={padTop} x2={rightX} y2={height - padBottom} stroke="var(--line)" />
                        {rows.map((row, index) => {
                            const delta = row.right - row.left;
                            const selected = focused != null && focused === row.label;
                            const clickable = Boolean(onFocus);
                            const color = tone(delta, favorable);
                            const activate = () => onFocus(spec.groupField || labelField, selected ? null : row.label);
                            return (
                                <g
                                    key={`${row.label}-${index}`}
                                    role={clickable ? "button" : undefined}
                                    tabIndex={clickable ? 0 : undefined}
                                    aria-pressed={clickable ? selected : undefined}
                                    aria-label={clickable ? `${row.label}: ${formatValue(row.left, format)} to ${formatValue(row.right, format)}` : undefined}
                                    opacity={focused != null && !selected ? 0.25 : 1}
                                    onClick={clickable ? activate : undefined}
                                    onKeyDown={clickable ? (event) => {
                                        if (event.key !== "Enter" && event.key !== " ") return;
                                        event.preventDefault();
                                        activate();
                                    } : undefined}
                                    style={clickable ? { cursor: "pointer" } : undefined}
                                >
                                    <line x1={leftX} y1={y(row.left)} x2={rightX} y2={y(row.right)} stroke="transparent" strokeWidth="9" />
                                    <line x1={leftX} y1={y(row.left)} x2={rightX} y2={y(row.right)} stroke={color} strokeWidth={selected ? 2 : 1} />
                                    <circle cx={leftX} cy={y(row.left)} r="2" fill={color} />
                                    <circle cx={rightX} cy={y(row.right)} r="2" fill={color} />
                                    <text x={leftX - 5} y={leftLabels[index] + 3} textAnchor="end" className="fill-[color:var(--muted)]" style={{ fontSize: 9.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{formatValue(row.left, format)}</text>
                                    <text x={rightX + 5} y={rightLabels[index] + 3} textAnchor="start" className="fill-[color:var(--ink)]" style={{ fontSize: 9.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                                        {formatValue(row.right, format)}<tspan className="fill-[color:var(--muted)]"> {row.label}</tspan>
                                    </text>
                                </g>
                            );
                        })}
                    </svg>
                ) : null}
            </div>
            <div className="mt-[3px] text-[9.5px] text-[color:var(--muted-soft)]">Slope shows change; crossings show rank changes.</div>
        </div>
    );
}
